import { PropertyCardContent } from "@/components/license-details/cards/property-card";
import { DetailsTileTitle } from "@/components/license-details/tile-info";
import { Card, CardHeader } from "@/components/ui/card";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { UserType } from "@/constants";
import type { PropertyResponseSchema } from "@/hera-api/client/types.gen";
import type {
  StrGovernmentLicenseSummary,
  StrListingDetail,
  StrPhoto,
} from "@/hera-api/str-government-api";
import { safeHttpUrl } from "@/lib/safe-http-url";
import { platformKey, platformLogo } from "@/lib/str-platform";
import { formatDateNoTimezone } from "@/lib/utils";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";

export { safeHttpUrl, platformKey, platformLogo };

export function dedupePhotos(photos: StrPhoto[]): StrPhoto[] {
  const seen = new Set<string>();
  return [...photos]
    .sort((left, right) => left.photo_index - right.photo_index)
    .filter((photo) => {
      const key = photo.image_id || photo.url;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

export function PlatformMark({
  platform,
  className = "h-6 w-auto",
}: {
  platform: string;
  className?: string;
}) {
  const logo = platformLogo(platform);
  if (!logo) {
    return <span className="text-sm font-medium">{platform}</span>;
  }
  return (
    <img
      src={logo}
      alt={platform}
      className={className}
      onError={(event) => {
        event.currentTarget.replaceWith(document.createTextNode(platform));
      }}
    />
  );
}

function occupancyCount(
  value: number | null,
  singular: string,
  plural: string,
): string | null {
  if (value == null) return null;
  const amount = Number.isInteger(value) ? String(value) : String(value);
  return `${amount} ${value === 1 ? singular : plural}`;
}

function firstSeenText(value: string | null | undefined): string | null {
  if (!value) return null;
  const label = formatDateNoTimezone(value);
  return label && label !== "Invalid date" ? label : null;
}

function earliestFirstSeen(listings: StrListingDetail[]): string | null {
  const dates = listings
    .map((listing) => listing.first_seen_on)
    .filter((value): value is string => Boolean(value))
    .sort();
  return dates[0] ?? null;
}

function ListingPhotos({
  photos,
  title,
}: {
  photos: StrPhoto[] | undefined;
  title: string;
}) {
  const items = useMemo(
    () =>
      [...(photos ?? [])]
        .sort((left, right) => left.photo_index - right.photo_index)
        .flatMap((photo) => {
          const url = safeHttpUrl(photo.url);
          return url ? [{ ...photo, url }] : [];
        }),
    [photos],
  );
  const [index, setIndex] = useState(0);
  const [failed, setFailed] = useState<Record<string, boolean>>({});
  if (items.length === 0) {
    return <div className="h-24 w-32 shrink-0 rounded bg-muted" />;
  }
  const current = items[Math.min(index, items.length - 1)];
  const step = (delta: number) => {
    setIndex((value) => (value + delta + items.length) % items.length);
  };
  return (
    <div className="relative h-24 w-32 shrink-0">
      {failed[current.url] ? (
        <div className="flex h-24 w-32 items-center justify-center rounded bg-muted px-1 text-center text-xs">
          Photo unavailable
        </div>
      ) : (
        <img
          src={current.url}
          alt={current.room_category || title}
          className="h-24 w-32 rounded object-cover"
          onError={() =>
            setFailed((prev) => ({ ...prev, [current.url]: true }))
          }
        />
      )}
      {items.length > 1 ? (
        <>
          <button
            type="button"
            aria-label="Previous photo"
            className="absolute top-1/2 left-0.5 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-white"
            onClick={() => step(-1)}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label="Next photo"
            className="absolute top-1/2 right-0.5 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-white"
            onClick={() => step(1)}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </>
      ) : null}
    </div>
  );
}

export function StrListingFacts({ listing }: { listing: StrListingDetail }) {
  const url = safeHttpUrl(listing.url);
  const hostProfile = safeHttpUrl(listing.host_profile_url);
  const title = listing.title || "Listing";
  const seen = firstSeenText(listing.first_seen_on);
  const facts = [
    occupancyCount(listing.bedrooms, "bedroom", "bedrooms"),
    occupancyCount(listing.bathrooms, "bathroom", "bathrooms"),
    occupancyCount(listing.guests, "guest", "guests"),
  ].filter((item): item is string => item != null);
  const mark = (
    <PlatformMark
      platform={listing.platform}
      className="h-6 w-auto shrink-0 object-contain"
    />
  );
  return (
    <article className="flex gap-3 rounded-md border p-3">
      <ListingPhotos photos={listing.photos} title={title} />
      <div className="min-w-0 space-y-1">
        <div className="flex items-center gap-2">
          {url ? (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Open ${listing.platform} listing`}
              className="inline-flex shrink-0"
            >
              {mark}
            </a>
          ) : (
            mark
          )}
          <h3 className="font-medium">{title}</h3>
        </div>
        {facts.length > 0 ? (
          <p className="text-sm text-muted-foreground">{facts.join(" · ")}</p>
        ) : null}
        {listing.host_name ? (
          hostProfile ? (
            <a
              href={hostProfile}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-muted-foreground underline"
            >
              {listing.host_name}
            </a>
          ) : (
            <p className="text-sm text-muted-foreground">{listing.host_name}</p>
          )
        ) : null}
        {seen ? (
          <p className="text-sm text-muted-foreground">First seen {seen}</p>
        ) : null}
      </div>
    </article>
  );
}

export function scopedStrLicenseId(
  licenseId: string,
  licenses: { license_id: string }[],
): string | null {
  return licenses.some((license) => license.license_id === licenseId)
    ? licenseId
    : null;
}

function strPropertyTile(
  address: string | null,
  latitude: number | null,
  longitude: number | null,
): PropertyResponseSchema {
  return {
    address1: address?.trim() || "",
    address2: null,
    city: "",
    state: "",
    zipcode: "",
    apn: null,
    latitude,
    longitude,
    display_address: address?.trim() || "Unknown",
    owner: null,
  };
}

export function StrPropertyDetailBody({
  address = null,
  addressUnverified = false,
  regions,
  latitude,
  longitude,
  listings,
  userType = UserType.GOVERNMENT,
}: {
  address?: string | null;
  addressUnverified?: boolean;
  regions: string[];
  latitude: number | null;
  longitude: number | null;
  listings: StrListingDetail[];
  userType?: UserType;
}) {
  return (
    <div className="space-y-4">
      {addressUnverified ? (
        <p className="rounded-md border border-orange-300 bg-orange-50 px-3 py-2 text-sm text-orange-950">
          This property has an exact listing location
          {address ? ` at ${address}` : ""}, but the address has not been
          verified.
        </p>
      ) : null}
      <Card>
        <CardHeader>
          <DetailsTileTitle name="Property" />
        </CardHeader>
        <PropertyCardContent
          property={strPropertyTile(address, latitude, longitude)}
          userType={userType}
          licenseId=""
          showRegistrationCertificate={false}
          firstSeen={earliestFirstSeen(listings)}
        />
      </Card>
      {regions.length > 0 ? (
        <section>
          <h2 className="font-semibold">Regions</h2>
          <p>{regions.join(", ")}</p>
        </section>
      ) : null}
      <section className="space-y-2">
        <h2 className="font-semibold">STR details</h2>
        {listings.map((listing) => (
          <StrListingFacts key={listing.id} listing={listing} />
        ))}
      </section>
    </div>
  );
}

export function licenseSummaryLabel(
  license: StrGovernmentLicenseSummary,
): string {
  return `${license.display_id || license.license_id} · ${license.status} · ${license.registrant_name || "Registrant"} · ${license.created_at || ""}`;
}

export function StrLicenseActions({
  licenses,
  onOpenLicense,
}: {
  licenses: StrGovernmentLicenseSummary[];
  onOpenLicense: (licenseId: string) => void;
}) {
  if (licenses.length === 0) return null;
  if (licenses.length === 1) {
    const license = licenses[0];
    return (
      <section className="space-y-2">
        <h2 className="font-semibold">License</h2>
        <button type="button" onClick={() => onOpenLicense(license.license_id)}>
          {licenseSummaryLabel(license)}
        </button>
      </section>
    );
  }
  return (
    <section className="space-y-2">
      <h2 className="font-semibold">Choose a license</h2>
      <p>This property has more than one current short-term rental license.</p>
      <ul className="space-y-2">
        {licenses.map((license) => (
          <li key={license.license_id}>
            <button
              type="button"
              onClick={() => onOpenLicense(license.license_id)}
            >
              {licenseSummaryLabel(license)}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function StrPropertyDetailsSheet({
  open,
  onOpenChange,
  title,
  loading,
  errorMessage,
  detail,
  licenses,
  onOpenLicense,
  actions,
  userType = UserType.GOVERNMENT,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  loading: boolean;
  errorMessage: string | null;
  userType?: UserType;
  detail: {
    address?: string | null;
    addressUnverified?: boolean;
    regions: string[];
    latitude: number | null;
    longitude: number | null;
    listings: StrListingDetail[];
  } | null;
  licenses: StrGovernmentLicenseSummary[];
  onOpenLicense: (licenseId: string) => void;
  actions?: ReactNode;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription className="sr-only">
            Short-term rental property details
          </SheetDescription>
        </SheetHeader>
        {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
        {errorMessage ? <p>{errorMessage}</p> : null}
        {!errorMessage && detail ? (
          <StrPropertyDetailBody
            address={detail.address}
            addressUnverified={detail.addressUnverified}
            regions={detail.regions}
            latitude={detail.latitude}
            longitude={detail.longitude}
            listings={detail.listings}
            userType={userType}
          />
        ) : null}
        {!errorMessage && !detail && loading ? <p>Loading property…</p> : null}
        <StrLicenseActions licenses={licenses} onOpenLicense={onOpenLicense} />
      </SheetContent>
    </Sheet>
  );
}
